import {List} from 'immutable';

import {addGenericErrorToast} from '@albert-io/atomic';
import type {ClassroomModelV1} from 'src/resources/augmented/Classroom/ClassroomModel.v1';
import getAssignmentsFilters from '../Assignments/getFilters';
import {getClassroomsQuery} from 'client/Classrooms/StudentDashboard/StudentDashboard.queries.js';
import getShouldRedirectUser from '../redirect/getShouldRedirectUser';
import getToDoSkillsQueryVariables from '../SkillPath/getQueryVariables';
import getUserDefaultRoute from '../redirect/getUserDefaultRoute';
import {history} from 'client/history';
import sessionStore from 'client/Session/SessionStore';
import type {StudentAssignment} from '../Assignments/StudentAssignment.types';
import type {StudentAssignmentsQueryHookResult} from '../Assignments/queries/StudentAssignments.queries.generated';
import type {ToDoSkillsQuery} from '../SkillPath/queries/StudentSkillPath.queries.generated';
import {useStudentAssignmentsLazyQuery} from '../Assignments/queries/StudentAssignments.queries.generated';
import {useStudentHomepageStore} from '../useStudentHomepageStore';
import {useToDoSkillsLazyQuery} from '../SkillPath/queries/StudentSkillPath.queries.generated';
import {Routes} from '../routes.constants';
import {useShallow} from 'zustand/react/shallow';

/**
 * Fetches the current user's classrooms, to-do assignments, and to-do skill paths, then updates the user's default route
 * based on those resources. Optionally redirects the user to that default route after the default route has been determined.
 */
export default function useSyncDefaultRoute() {
  const studentId = sessionStore.getUserId();
  const {setUserDefaultRoute, setClassrooms, setAreClassroomsLoading} = useStudentHomepageStore(
    useShallow((state) => ({
      setUserDefaultRoute: state.setUserDefaultRoute,
      setClassrooms: state.setClassrooms,
      setAreClassroomsLoading: state.setAreClassroomsLoading
    }))
  );

  const assignmentsFilters = getAssignmentsFilters(undefined, 'toDo');
  const [fetchStudentAssignments] = useStudentAssignmentsLazyQuery({
    variables: {filters: assignmentsFilters}
  });

  const [fetchToDoSkills] = useToDoSkillsLazyQuery();

  async function fetchResources() {
    let classrooms: List<ClassroomModelV1> = List();
    let toDoAssignments: StudentAssignment[] = [];
    let toDoSkills: NonNullable<NonNullable<ToDoSkillsQuery['subjects']>['results']> = [];

    const promises = [
      fetchStudentAssignments(),
      getClassroomsQuery(studentId).getResourcePromise()
    ];

    try {
      const [fetchedToDoAssignments, fetchedClassrooms]: [
        StudentAssignmentsQueryHookResult,
        List<ClassroomModelV1>
      ] = await Promise.all(promises);

      classrooms = fetchedClassrooms;
      setClassrooms(fetchedClassrooms);
      setAreClassroomsLoading(false);
      toDoAssignments = fetchedToDoAssignments.data?.currentUser?.studentAssignments.results ?? [];

      const toDoSkillsQueryVariables = getToDoSkillsQueryVariables(
        undefined,
        fetchedClassrooms.map((fetchedClassroom) => fetchedClassroom.getId()).toArray()
      );

      const fetchedToDoSkills = await fetchToDoSkills(toDoSkillsQueryVariables);
      toDoSkills = fetchedToDoSkills.data?.subjects?.results ?? [];
    } catch (error) {
      console.error(error);
      addGenericErrorToast();
    }

    return {classrooms, toDoAssignments, toDoSkills};
  }

  async function syncDefaultRoute(
    classroomId: string | undefined,
    pathname: string,
    skipRedirect = false
  ) {
    setUserDefaultRoute(null);

    try {
      const {classrooms, toDoAssignments, toDoSkills} = await fetchResources();

      const userDefaultRoute = getUserDefaultRoute({
        classrooms,
        toDoAssignments,
        toDoSkills
      });

      setUserDefaultRoute(userDefaultRoute);

      const shouldRedirectUser = getShouldRedirectUser({
        classroomId,
        classrooms,
        pathname
      });

      if (!skipRedirect && shouldRedirectUser) {
        history.replace(userDefaultRoute);
      }
    } catch (error) {
      console.error(error);
      addGenericErrorToast();
      setUserDefaultRoute(Routes.Assignments);

      if (!skipRedirect) {
        history.replace(Routes.Assignments);
      }
    }
  }

  return {syncDefaultRoute};
}
